import React, { useState } from 'react';
import { Badge, Button, Collapse, Space, Table, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import { ACLCheck } from 'shared/Helpers';
import { formatDate } from 'components/NewDealComponents/constants/dealListFormat';
import type { Scope } from 'types/permissions';

/**
 * One deal hidden from the Smart Calendar (`deal.show_on_smart_calendar = false`) that would otherwise be
 * in the current Active/Expired view. Mirrors the API's `HiddenCalendarDealDto` on
 * `GET /deals/calendar`. Kept as its own type rather than a Gantt `RenewalRecord`: hidden deals
 * never enter the Bryntum task tree, so the CONTRACTS badge, TCV roll-ups and project window ignore them.
 */
export interface HiddenCalendarDeal {
  /** Deal UUID — the id `PATCH /deals/:id` takes, so Unhide needs no lookup. */
  dealId: string;
  deal_number: string;
  subject: string | null;
  /** Primary supplier name. */
  vendorName: string | null;
  /** ISO date YYYY-MM-DD. */
  contract_start_date: string | null;
  /** ISO date YYYY-MM-DD. */
  contract_end_date: string | null;
  /** Human-readable status label, formatted by the API. */
  status: string;
}

/**
 * Scopes that may unhide a deal — the same pair `PATCH /deals/:id` requires, so the button is only offered
 * to callers the API would accept. No new permission is introduced.
 */
export const UNHIDE_SCOPES: readonly Scope[] = ['update:contract_all', 'update:own_company_deals'];

const PANEL_KEY = 'hidden-deals';

export interface HiddenDealsSectionProps {
  /** Hidden deals for the current window. The section renders nothing when this is empty. */
  hiddenDeals: HiddenCalendarDeal[];
  /**
   * Flips `show_on_smart_calendar` back to true for one deal and refreshes the calendar. Resolves once the
   * refresh has landed; the row's Unhide button shows a spinner until then.
   */
  onUnhide: (dealId: string) => Promise<void>;
  /** Opens the deal's detail page. When omitted the Deal # renders as plain text. */
  onOpenDeal?: (dealId: string) => void;
}

/**
 * Collapsed "Hidden Deals" row (styled like a Gantt vendor row, count in a badge) rendered under the Smart
 * Calendar Gantt. Lists the deals a
 * user has unticked "Show on Smart Calendar" for, with the contract dates intact, and offers a one-click
 * Unhide for callers holding a deal-update scope.
 *
 * Collapsed by default so a calendar with hidden deals looks exactly like one without until the user asks;
 * absent entirely when there is nothing hidden, so the legacy (non-early-access) calendar — whose payload
 * carries no `hiddenDeals` — is untouched.
 * @param {HiddenDealsSectionProps} props - Hidden deals plus the unhide/open handlers.
 * @returns {React.ReactElement | null} The collapsible section, or null when no deal is hidden.
 */
export const HiddenDealsSection: React.FC<HiddenDealsSectionProps> = ({ hiddenDeals, onUnhide, onOpenDeal }) => {
  // The deal currently being unhidden. One at a time is enough: each unhide ends in a full calendar
  // refetch, which re-renders this list from the new payload.
  const [unhidingDealId, setUnhidingDealId] = useState<string | null>(null);
  // Controlled so the caret drawn inside the header (see below) can follow the open state.
  const [expanded, setExpanded] = useState(false);
  const canUnhide = ACLCheck([...UNHIDE_SCOPES]);

  if (hiddenDeals.length === 0) return null;

  const handleUnhide = async (dealId: string): Promise<void> => {
    setUnhidingDealId(dealId);
    try {
      await onUnhide(dealId);
    } finally {
      setUnhidingDealId(null);
    }
  };

  const columns: ColumnsType<HiddenCalendarDeal> = [
    {
      title: 'Deal #',
      dataIndex: 'deal_number',
      key: 'deal_number',
      render: (dealNumber: string, row) =>
        onOpenDeal ? (
          <Button type="link" className="!p-0 !h-auto" onClick={() => onOpenDeal(row.dealId)}>
            {dealNumber}
          </Button>
        ) : (
          dealNumber
        )
    },
    { title: 'Deal', dataIndex: 'subject', key: 'subject', render: (v: string | null) => v ?? '-' },
    { title: 'Supplier', dataIndex: 'vendorName', key: 'vendorName', render: (v: string | null) => v ?? '-' },
    {
      title: 'Contract Start',
      dataIndex: 'contract_start_date',
      key: 'contract_start_date',
      render: (v: string | null) => formatDate(v)
    },
    {
      title: 'Contract End',
      dataIndex: 'contract_end_date',
      key: 'contract_end_date',
      render: (v: string | null) => formatDate(v)
    },
    { title: 'Status', dataIndex: 'status', key: 'status' },
    {
      title: 'Action',
      key: 'action',
      align: 'center',
      // Unhide needs a deal-update scope (the same pair PATCH /deals/:id requires); without one the button is
      // shown disabled.
      render: (_: unknown, row) => (
        <Button
          size="small"
          icon={<EyeOutlined />}
          loading={unhidingDealId === row.dealId}
          disabled={!canUnhide || (unhidingDealId !== null && unhidingDealId !== row.dealId)}
          onClick={() => void handleUnhide(row.dealId)}
        >
          Unhide
        </Button>
      )
    }
  ];

  // A pixel match for a Gantt vendor row (e.g. "› 🏛 Zoom Video Communications (3)") so the section
  // reads as one more row of the calendar:
  //  • caret — Bryntum's own tree expander (`b-icon-tree-expand` / `-collapse`, Font Awesome angle
  //    glyphs in #adafaf, 1.25em wide, 0.55em side margins), whose stylesheet is already on this page
  //    because the Gantt imports it;
  //  • name — <strong> in #444 at 14px, the grid's readonly cell colour and base size;
  //  • badge — the app's default antd Badge (bryntum-gantt.scss restyles `.ant-badge-count` globally),
  //    wrapped in a tooltip exactly as the vendor row does.
  // The row is padded 0.5em like a Bryntum grid cell, so the caret lands where the Gantt's does. Both
  // caret and text sit in one `items-center` row rather than antd's separate arrow slot, so they cannot
  // drift apart vertically.
  const header = (
    <span className="flex items-center text-[14px] text-[#444]">
      <i
        className={`b-icon ${expanded ? 'b-icon-tree-collapse' : 'b-icon-tree-expand'} mx-[0.55em] relative top-px`}
        data-testid="hidden-deals-caret"
        aria-hidden="true"
      />
      <Space size={3}>
        <EyeInvisibleOutlined className="opacity-70" />
        <strong>Hidden Deals</strong>
        <Tooltip title="Count of hidden deals">
          <Badge count={hiddenDeals.length} showZero overflowCount={99999} />
        </Tooltip>
      </Space>
    </span>
  );

  return (
    <Collapse
      ghost
      className="mt-2"
      data-testid="hidden-deals-section"
      activeKey={expanded ? [PANEL_KEY] : []}
      onChange={(keys) => setExpanded(([] as string[]).concat(keys).includes(PANEL_KEY))}
    >
      <Collapse.Panel
        header={header}
        key={PANEL_KEY}
        showArrow={false}
        className="[&>.ant-collapse-header]:!px-[0.5em] [&>.ant-collapse-header]:!py-[0.5em]"
      >
        <Table<HiddenCalendarDeal>
          size="small"
          rowKey="dealId"
          pagination={false}
          columns={columns}
          dataSource={hiddenDeals}
        />
      </Collapse.Panel>
    </Collapse>
  );
};

export default HiddenDealsSection;
